if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.content = this.querySelector('.product-media-modal__content');
      this.dialog = this.querySelector('[role="dialog"]');
      this.previous = this.querySelector('.as-media-viewer__previous');
      this.next = this.querySelector('.as-media-viewer__next');
      this.counter = this.querySelector('.as-media-viewer__counter');
      this.thumbnails = [...this.querySelectorAll('[data-viewer-media-id]')];
      this.zoomButtons = [...this.querySelectorAll('[data-viewer-zoom]')];
      this.pointers = new Map();
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      window.addEventListener('resize', () => this.updateViewport());
      this.dialog.addEventListener('pointerup', (event) => event.stopPropagation());
      this.previous?.addEventListener('click', () => this.navigate(-1));
      this.next?.addEventListener('click', () => this.navigate(1));
      this.thumbnails.forEach((button) => button.addEventListener('click', () => {
        const item = this.availableMedia().find((media) => media.dataset.mediaId === button.dataset.viewerMediaId);
        if (item) this.activate(item);
      }));
      this.zoomButtons.forEach((button) => button.addEventListener('click', () => {
        const action = button.dataset.viewerZoom;
        this.zoomTo(action === 'reset' ? 1 : this.scale + (action === 'in' ? .5 : -.5));
      }));
      this.addEventListener('keydown', (event) => {
        if (!this.hasAttribute('open')) return;
        if (event.key === 'Tab') {
          const focusable = [...this.querySelectorAll('button:not([disabled]), a[href], input, select, textarea, video, iframe, model-viewer, [tabindex]:not([tabindex="-1"])')].filter((element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (first && ((event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) || (!event.shiftKey && document.activeElement === last))) {
            event.preventDefault();
            event.stopPropagation();
            (event.shiftKey ? last : first).focus();
          }
          return;
        }
        if (event.target.closest('input, textarea, select, video, iframe, model-viewer')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigate(event.key === 'ArrowLeft' ? -1 : 1);
        }
      });
      this.content.addEventListener('dragstart', (event) => event.preventDefault());
      this.content.addEventListener('dblclick', (event) => {
        if (this.active?.tagName !== 'IMG') return;
        event.preventDefault();
        this.zoomTo(this.scale > 1 ? 1 : 2, event.clientX, event.clientY);
      });
      this.content.addEventListener('pointerdown', (event) => this.pointerDown(event));
      this.content.addEventListener('pointermove', (event) => this.pointerMove(event));
      this.content.addEventListener('pointerup', (event) => this.pointerEnd(event));
      this.content.addEventListener('pointercancel', (event) => this.pointerEnd(event, true));
      this.resizeObserver = new ResizeObserver(() => this.renderZoom());
      this.resizeObserver.observe(this.content);
      this.filterObserver = new MutationObserver((records) => {
        if (records.some((record) => record.type === 'childList' || record.target.hasAttribute('data-media-id'))) {
          if (this.hasAttribute('open')) this.syncMedia();
        }
      });
      this.filterObserver.observe(this.content, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'style'] });
    }

    availableMedia() {
      const gallery = document.getElementById('MediaGallery-' + this.dataset.section);
      const galleryItems = [...(gallery?.querySelectorAll('.thumbnail-list__item[data-target]') || [])];
      const visibleIds = new Set(galleryItems.filter((item) => !item.hidden && getComputedStyle(item).display !== 'none').map((item) => item.dataset.target.split('-').pop()));
      return [...this.content.querySelectorAll(':scope > [data-media-id]')].filter((item) => !item.hidden && item.style.display !== 'none' && (!galleryItems.length || visibleIds.has(item.dataset.mediaId)));
    }

    show(opener) {
      this.updateViewport();
      super.show(opener);
      this.syncMedia(opener.getAttribute('data-media-id'));
    }

    updateViewport() {
      const bar = document.getElementById('PBarNextFrameWrapper');
      const bounds = bar?.getBoundingClientRect();
      const reservedHeight = bounds && bounds.width > 0 && bounds.height < 200 ? Math.max(0, bounds.height) : 0;
      this.style.setProperty('--as-viewer-bottom', reservedHeight + 'px');
      if (bar && this.previewBar !== bar) {
        this.previewBar = bar;
        this.barObserver?.disconnect();
        this.barObserver = new ResizeObserver(() => this.updateViewport());
        this.barObserver.observe(bar);
      }
    }

    hide() {
      this.pointers.clear();
      this.classList.remove('is-dragging');
      this.zoomTo(1);
      super.hide();
    }

    syncMedia(preferredId) {
      const items = this.availableMedia();
      this.items = items;
      const ids = new Set(items.map((item) => item.dataset.mediaId));
      const visibleButtons = this.thumbnails.filter((button) => ids.has(button.dataset.viewerMediaId));
      this.thumbnails.forEach((button) => { button.hidden = !ids.has(button.dataset.viewerMediaId); });
      visibleButtons.forEach((button, index) => {
        const label = button.dataset.label.replace('[index]', String(index + 1));
        const alt = button.querySelector('img')?.alt;
        button.setAttribute('aria-label', alt ? label + ': ' + alt : label);
      });
      const current = items.find((item) => item.dataset.mediaId === preferredId) || (items.includes(this.active) ? this.active : items[0]);
      if (current) this.activate(current);
    }

    activate(item) {
      if (this.active !== item) window.pauseAllMedia();
      this.content.querySelectorAll(':scope > [data-media-id]').forEach((media) => media.classList.toggle('active', media === item));
      this.active = item;
      this.pointers.clear();
      this.classList.remove('is-dragging');
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      this.content.scrollLeft = 0;
      this.content.scrollTop = 0;
      if (item.tagName === 'IMG') {
        item.draggable = false;
        item.sizes = '(min-width: 750px) min(1200px, 90vw), 100vw';
      } else if (item.tagName === 'DEFERRED-MEDIA') {
        item.loadContent(false);
      }
      this.renderZoom();
      this.updateNavigation();
    }

    navigate(direction) {
      this.items = this.availableMedia();
      const index = this.items.indexOf(this.active);
      const item = this.items[index + direction];
      if (item) this.activate(item);
    }

    updateNavigation() {
      const index = this.items.indexOf(this.active);
      if (this.counter) this.counter.textContent = (index + 1) + ' / ' + this.items.length;
      if (this.previous) { this.previous.disabled = index <= 0; this.previous.hidden = this.items.length < 2; }
      if (this.next) { this.next.disabled = index >= this.items.length - 1; this.next.hidden = this.items.length < 2; }
      let selected;
      this.thumbnails.forEach((button) => {
        const current = button.dataset.viewerMediaId === this.active?.dataset.mediaId;
        if (current) { button.setAttribute('aria-current', 'true'); selected = button; }
        else button.removeAttribute('aria-current');
      });
      if (selected) {
        const rail = selected.parentElement;
        const buttonBounds = selected.getBoundingClientRect();
        const railBounds = rail.getBoundingClientRect();
        if (buttonBounds.left < railBounds.left + 8) rail.scrollLeft -= railBounds.left + 8 - buttonBounds.left;
        else if (buttonBounds.right > railBounds.right - 8) rail.scrollLeft += buttonBounds.right - railBounds.right + 8;
      }
    }

    zoomTo(scale, clientX, clientY) {
      if (this.active?.tagName !== 'IMG') scale = 1;
      const nextScale = Math.max(1, Math.min(4, scale));
      const bounds = this.content.getBoundingClientRect();
      const x = (clientX ?? bounds.left + bounds.width / 2) - bounds.left - bounds.width / 2;
      const y = (clientY ?? bounds.top + bounds.height / 2) - bounds.top - bounds.height / 2;
      const ratio = nextScale / this.scale;
      this.panX = x - (x - this.panX) * ratio;
      this.panY = y - (y - this.panY) * ratio;
      this.scale = nextScale;
      if (nextScale === 1) { this.panX = 0; this.panY = 0; }
      this.renderZoom();
    }

    renderZoom() {
      const image = this.active?.tagName === 'IMG' ? this.active : null;
      if (image) {
        const maxX = Math.max(0, (image.offsetWidth * this.scale - this.content.clientWidth) / 2);
        const maxY = Math.max(0, (image.offsetHeight * this.scale - this.content.clientHeight) / 2);
        this.panX = Math.max(-maxX, Math.min(maxX, this.panX));
        this.panY = Math.max(-maxY, Math.min(maxY, this.panY));
      }
      this.content.style.setProperty('--as-pan-x', this.panX + 'px');
      this.content.style.setProperty('--as-pan-y', this.panY + 'px');
      this.content.style.setProperty('--as-zoom', this.scale);
      this.classList.toggle('is-zoomed', this.scale > 1);
      this.zoomButtons.forEach((button) => {
        const action = button.dataset.viewerZoom;
        button.disabled = !image || (action === 'in' ? this.scale >= 4 : this.scale <= 1);
        if (action === 'reset') button.textContent = Math.round(this.scale * 100) + '%';
      });
    }

    pointerDown(event) {
      if (this.active?.tagName !== 'IMG' || event.button !== 0 || !event.target.closest('img.active')) return;
      event.preventDefault();
      this.content.setPointerCapture(event.pointerId);
      if (!this.pointers.size) this.didPinch = false;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.startGesture();
      this.classList.add('is-dragging');
    }

    startGesture() {
      const points = [...this.pointers.values()];
      const first = points[0];
      if (!first) return;
      this.gesture = { x: first.x, y: first.y, panX: this.panX, panY: this.panY, scale: this.scale };
      if (points.length > 1) {
        this.didPinch = true;
        this.gesture.distance = Math.max(1, Math.hypot(points[1].x - first.x, points[1].y - first.y));
        this.gesture.x = (first.x + points[1].x) / 2;
        this.gesture.y = (first.y + points[1].y) / 2;
      }
    }

    pointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const points = [...this.pointers.values()];
      const gesture = this.gesture;
      if (points.length > 1) {
        const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
        this.scale = Math.max(1, Math.min(4, gesture.scale * distance / gesture.distance));
        const bounds = this.content.getBoundingClientRect();
        const centerX = bounds.left + bounds.width / 2;
        const centerY = bounds.top + bounds.height / 2;
        const ratio = this.scale / gesture.scale;
        this.panX = (points[0].x + points[1].x) / 2 - centerX - (gesture.x - centerX - gesture.panX) * ratio;
        this.panY = (points[0].y + points[1].y) / 2 - centerY - (gesture.y - centerY - gesture.panY) * ratio;
      } else if (this.scale > 1) {
        this.panX = gesture.panX + event.clientX - gesture.x;
        this.panY = gesture.panY + event.clientY - gesture.y;
      }
      this.renderZoom();
    }

    pointerEnd(event, cancelled = false) {
      if (!this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      event.stopPropagation();
      const dx = event.clientX - this.gesture.x;
      const dy = event.clientY - this.gesture.y;
      const swipe = !cancelled && !this.didPinch && this.scale === 1 && this.pointers.size === 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.2;
      this.pointers.delete(event.pointerId);
      if (this.pointers.size) this.startGesture();
      else this.classList.remove('is-dragging');
      if (swipe) this.navigate(dx < 0 ? 1 : -1);
    }
  });
}
